import { cn } from "@/common/helpers/utils";
import { CopyToClipboard } from "@/components/copy-to-clipboard/copy-to-clipboard";
import {
  buildSnapshotCopyTargets,
  findFirstOverlappingMatchIndex,
  findSearchMatches,
  getJsonTokenPropertyName,
  splitJsonTokensIntoRows,
  type JsonToken,
  type JsonTokenKind,
  type SearchMatch,
} from "@/components/dat-snapshot/_helpers/dat-snapshot-json";
import { bindDatSnapshotTextSelection } from "@/components/dat-snapshot/_helpers/dat-snapshot-selection";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { type ReactNode, type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface SnapshotQuickJumpRequest {
  id: number;
  start: number;
}

interface DatSnapshotJsonProps {
  quickJumpRequest?: SnapshotQuickJumpRequest;
  search: string;
  tokens: JsonToken[];
  value: string;
}

const JSON_TOKEN_CLASSES: Record<JsonTokenKind, string | undefined> = {
  keyword: "text-amber-700 dark:text-amber-300",
  number: "text-violet-700 dark:text-violet-300",
  plain: undefined,
  property: "text-sky-700 dark:text-sky-300",
  punctuation: "text-muted-foreground",
  string: "text-emerald-700 dark:text-emerald-300",
};

const QUICK_JUMP_HIGHLIGHT_DURATION_MS = 1_800;

function renderTokenText(
  token: JsonToken,
  matches: SearchMatch[],
  activeMatchIndex: number,
  activeMatchRef: RefObject<HTMLElement | null>,
): ReactNode {
  const firstMatchIndex = findFirstOverlappingMatchIndex(matches, token.start);

  if (firstMatchIndex >= matches.length || matches[firstMatchIndex].start >= token.end) {
    return token.text;
  }

  const fragments: ReactNode[] = [];
  let cursor = token.start;

  for (let matchIndex = firstMatchIndex; matchIndex < matches.length; matchIndex += 1) {
    const match = matches[matchIndex];

    if (match.start >= token.end) {
      break;
    }

    const matchStart = Math.max(match.start, token.start);
    const matchEnd = Math.min(match.end, token.end);

    if (matchStart > cursor) {
      fragments.push(token.text.slice(cursor - token.start, matchStart - token.start));
    }

    const isActive = match.index === activeMatchIndex;
    const isMatchStart = matchStart === match.start;

    fragments.push(
      <mark
        key={`${match.index}-${matchStart}`}
        ref={isActive && isMatchStart ? activeMatchRef : undefined}
        data-current-match={isActive ? "true" : undefined}
        className={cn(
          "rounded-[2px] bg-amber-200/60 px-0 text-inherit dark:bg-amber-300/15",
          isActive && "bg-amber-300/70 ring-1 ring-amber-600/60 ring-inset dark:bg-amber-300/25 dark:ring-amber-300/60",
        )}
      >
        {token.text.slice(matchStart - token.start, matchEnd - token.start)}
      </mark>,
    );
    cursor = matchEnd;
  }

  if (cursor < token.end) {
    fragments.push(token.text.slice(cursor - token.start));
  }

  return fragments;
}

export function DatSnapshotJson({ quickJumpRequest, search, tokens, value }: DatSnapshotJsonProps) {
  const { t } = useTranslation("fab");
  const matches = useMemo(() => findSearchMatches(value, search), [search, value]);
  const rows = useMemo(() => splitJsonTokensIntoRows(tokens), [tokens]);
  const copyTargets = useMemo(
    () =>
      buildSnapshotCopyTargets(
        value,
        tokens.filter((token) => token.kind === "property"),
      ),
    [tokens, value],
  );
  const [matchNavigation, setMatchNavigation] = useState({ index: 0, search: "" });
  const activeMatchRef = useRef<HTMLElement>(null);
  const snapshotContentRef = useRef<HTMLPreElement>(null);
  const activeMatchIndex =
    matchNavigation.search === search && matches.length > 0 ? Math.min(matchNavigation.index, matches.length - 1) : 0;

  useEffect(() => {
    const activeMatch = activeMatchRef.current;

    if (activeMatch && typeof activeMatch.scrollIntoView === "function") {
      activeMatch.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }
  }, [activeMatchIndex, search]);

  useEffect(() => {
    const snapshotContent = snapshotContentRef.current;

    if (!snapshotContent || !quickJumpRequest) {
      return;
    }

    const target = snapshotContent.querySelector<HTMLElement>(`[data-json-property-start="${quickJumpRequest.start}"]`);

    if (!target) {
      return;
    }

    target.dataset.quickJumpHighlight = "true";

    if (typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }

    const highlightTimeout = window.setTimeout(() => {
      delete target.dataset.quickJumpHighlight;
    }, QUICK_JUMP_HIGHLIGHT_DURATION_MS);

    return () => {
      window.clearTimeout(highlightTimeout);
      delete target.dataset.quickJumpHighlight;
    };
  }, [quickJumpRequest, value]);

  useEffect(() => {
    const snapshotContent = snapshotContentRef.current;
    return snapshotContent ? bindDatSnapshotTextSelection(snapshotContent) : undefined;
  }, []);

  const moveToMatch = (direction: 1 | -1): void => {
    if (matches.length === 0) {
      return;
    }

    setMatchNavigation({
      index: (activeMatchIndex + direction + matches.length) % matches.length,
      search,
    });
  };

  return (
    <div
      className={cn(
        "grid min-h-0 overflow-hidden rounded-lg border",
        search ? "grid-rows-[auto_1fr]" : "grid-rows-[1fr]",
      )}
    >
      {search ? (
        <div className="bg-muted/35 flex min-h-12 min-w-0 flex-wrap items-center justify-end gap-2 border-b p-2">
          <output aria-live="polite" className="text-muted-foreground min-w-14 text-center text-xs tabular-nums">
            {matches.length > 0 ? `${activeMatchIndex + 1} / ${matches.length}` : "0 / 0"}
          </output>
          <Button
            type="button"
            size={ButtonSize.Icon}
            variant={ButtonVariant.Ghost}
            colorVariant={ButtonColorVariant.Secondary}
            aria-label={t("PreviousDatSnapshotSearchResult")}
            disabled={matches.length === 0}
            onClick={() => moveToMatch(-1)}
          >
            <ChevronUpIcon />
          </Button>
          <Button
            type="button"
            size={ButtonSize.Icon}
            variant={ButtonVariant.Ghost}
            colorVariant={ButtonColorVariant.Secondary}
            aria-label={t("NextDatSnapshotSearchResult")}
            disabled={matches.length === 0}
            onClick={() => moveToMatch(1)}
          >
            <ChevronDownIcon />
          </Button>
        </div>
      ) : null}
      <pre
        ref={snapshotContentRef}
        className="bg-muted/20 min-h-0 overflow-auto p-4 text-xs leading-5 select-text sm:text-sm"
      >
        <code className="font-mono">
          {rows.map((row, rowIndex) => {
            const copyTarget = row.propertyToken ? copyTargets.get(row.propertyToken.start) : undefined;

            return (
              <span key={row.start} data-snapshot-json-row={copyTarget?.key} className="group">
                {row.tokens.map((token) => (
                  <span
                    key={token.start}
                    data-json-property-key={getJsonTokenPropertyName(token)}
                    data-json-property-start={token.kind === "property" ? token.start : undefined}
                    data-json-token={token.kind}
                    className={cn(
                      JSON_TOKEN_CLASSES[token.kind],
                      token.kind === "property" &&
                        "rounded-sm transition-[background-color,box-shadow,color] duration-300 data-[quick-jump-highlight=true]:bg-orange-300 data-[quick-jump-highlight=true]:text-black data-[quick-jump-highlight=true]:ring-2 data-[quick-jump-highlight=true]:ring-orange-500 data-[quick-jump-highlight=true]:ring-offset-1 data-[quick-jump-highlight=true]:ring-offset-transparent",
                    )}
                  >
                    {renderTokenText(token, matches, activeMatchIndex, activeMatchRef)}
                  </span>
                ))}
                {copyTarget ? (
                  <span
                    data-snapshot-row-action={copyTarget.key}
                    className="ml-2 inline-flex h-5 items-center align-middle opacity-0 transition-opacity select-none group-focus-within:opacity-100 group-hover:opacity-100 [&>button]:h-5 [&>button]:w-5"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.stopPropagation();
                      }
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <CopyToClipboard
                      colorVariant={ButtonColorVariant.Secondary}
                      value={copyTarget.serialize}
                      tooltip={t("CopyDatSnapshotRow", { key: copyTarget.key })}
                    />
                  </span>
                ) : null}
                {rowIndex < rows.length - 1 ? "\n" : null}
              </span>
            );
          })}
        </code>
      </pre>
    </div>
  );
}
